<template>

  <div>
    <b-row>
      <b-col class="text-left">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          class="my-1"
          @click="showFilters = !showFilters"
        >
          <FeatherIcon icon="FilterIcon" />
          Filters
        </b-button>
      </b-col>
      <b-col
        v-if="$permissions().hasPermission('create user')"
        class="text-right"
      >
        <b-button
          variant="secondary"
          @click="$router.push('/users/invite')"
        >
          <span class="text-nowrap">Invite User</span>
        </b-button>
      </b-col>
    </b-row>
    <b-card
      v-if="showFilters"
      no-body
      class="mb-2"
    >
      <b-form
        class="m-2"
        @keyup.enter="fetchList"
      >
        <!-- Filters -->
        <b-row>
          <b-col
            lg="12"
            md="12"
          >
            <b-row>
              <!-- Name filter -->
              <b-col
                lg="6"
                mdv="6"
              >
                <b-form-group
                  label="Company"
                  label-for="company-filter"
                >
                  <v-select
                    id="company-filter"
                    v-model="selectedCompany"
                    :options="companiesList"
                    :clearable="false"
                    input-id="status"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="5"
                mdv="5"
              >
                <b-form-group
                  label="Search"
                  label-for="search"
                >
                  <b-form-input
                    v-model="searchQuery"
                    class="d-inline-block mr-1"
                    placeholder="Search..."
                    autocomplete="off"
                  />
                </b-form-group>
              </b-col>
              <b-col class="text-right">
                <b-button
                  class="mt-2"
                  type="button"
                  variant="primary"
                  @click="fetchData"
                >
                  <span class="text-nowrap">Filter</span>
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-actions
        ref="loadingCard"
        no-actions
      >
        <b-table
          ref="refUserListTable"
          class="position-relative users-table"
          :items="userList"
          responsive
          :fields="tableColumns"
          stacked="sm"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
        >

          <!-- Column: Created at -->
          <template #cell(created_at)="data">
            {{ data.item.created_at | formatDate }}
          </template>

          <!-- Column: Last login at -->x
          <template #cell(last_login_at)="data">
            <div
              v-if="data.item.invitation && data.item.status !== 'accepted'"
              class="d-flex align-items-center"
            >
              <b-badge
                v-if="data.item.status === 'declined'"
                pill
                variant="danger"
              >
                Declined
              </b-badge>
              <b-badge
                v-if="data.item.status === 'sent'"
                pill
                variant="info"
              >
                Invited
              </b-badge>
            </div>
            <span v-if="!data.item.invitation && data.item.last_login_at">{{ data.item.last_login_at | formatDate }}</span>
          </template>

          <!-- Column: User -->
          <template #cell(name)="data">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="32"
                  :src="data.item.avatar"
                  :text="avatarText(data.item.name)"
                  :to="$permissions().hasPermission('edit user') ? { name: 'user-edit', params: { userId: data.item.id } } : null"
                />
              </template>
              <b-link
                v-if="!data.item.invitation && $permissions().hasPermission('edit user')"
                :to="{ name: 'user-edit', params: { userId: data.item.id } }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.name }}
              </b-link>
              <span
                v-if="data.item.invitation || !$permissions().hasPermission('edit user')"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.name }}
              </span>
              <small
                v-if="data.item.roles"
                class="text-muted"
              >{{ data.item.roles.join(', ') }}</small>
            </b-media>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-button-group>
              <!-- Edit user button -->
              <b-button
                v-if="$permissions().hasPermission('edit user')"
                v-b-tooltip.hover.top="'Edit user'"
                variant="primary"
                size="sm"
                :disabled="data.item.invitation"
                @click="edit(data.item.id)"
              >
                <feather-icon
                  icon="EditIcon"
                  class="d-inline"
                />
              </b-button>
              <!-- Delete user button -->
              <b-button
                v-if="!data.item.invitation && $permissions().hasPermission('delete user')"
                v-b-tooltip.hover.top="'Delete user from the company'"
                variant="outline-danger"
                size="sm"
                @click="deleteUser(data.item.id)"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="d-inline"
                />
              </b-button>
              <!-- Delete invitation button -->
              <b-button
                v-if="data.item.invitation && $permissions().hasPermission('create user')"
                v-b-tooltip.hover.top="'Delete invitation from the company'"
                variant="outline-danger"
                size="sm"
                @click="deleteInvitation(data.item.id)"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="d-inline"
                />
              </b-button>
            </b-button-group>
          </template>

        </b-table>
      </b-card-actions>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  VBTooltip,
  BBadge,
  BButtonGroup,
  BForm,
  BFormGroup,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import handleError from '@/views/components/errorHandler'
import useUsersList from './useUsersList'
import userStoreModule from '../userStoreModule'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import vSelect from 'vue-select'

export default {
  components: {
    vSelect,
    BFormGroup,
    BForm,
    BCard,
    BCardActions,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BButtonGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      selectedCompany: null,
      companyList: [],
      mergeColumns: ['id', 'email', 'name', 'created_at', 'last_login_at', 'status', 'actions', 'roles'],
      existingUsers: [],
      invitedUsers: [],
      showFilters: true,
    }
  },
  computed: {
    companiesList() {
      const filtered = []
      this.companyList.forEach(item => {
        filtered.push({
          label: item.name,
          value: item.id,
        })
      })

      return filtered
    },
    userList() {
      // Format merged list properly
      const fullList = []
      // Existing users
      Object.values(this.existingUsers).forEach(value => {
        const myObj = {}
        this.mergeColumns.forEach(column => {
          if (value[column]) {
            myObj[column] = value[column]
          } else {
            myObj[column] = null
          }
        })
        fullList.push(myObj)
      })
      // Invitations
      Object.values(this.invitedUsers).forEach(value => {
        const myObj = {}
        this.mergeColumns.forEach(column => {
          if (value[column]) {
            myObj[column] = value[column]
          } else {
            myObj[column] = null
          }
        })
        myObj.invitation = true
        fullList.push(myObj)
      })
      return fullList
    },
  },
  created() {
    this.companyList = this.$profile().getUserAndAuthorizedCompanies()
  },
  methods: {
    fetchData() {
      if (!this.selectedCompany) {
        return
      }
      this.$refs.loadingCard.showLoading = true
      this.fetchUsers(this.selectedCompany.value, this, users => {
        this.$refs.loadingCard.showLoading = false
        this.existingUsers = users
      })
      this.fetchInvitedUsers(this.selectedCompany.value, this, users => {
        this.$refs.loadingCard.showLoading = false
        this.invitedUsers = users
      })
    },
    edit(id) {
      this.$router.push({ name: 'user-edit', params: { companyId: this.selectedCompany.value, userId: id } })
    },
    deleteUser(id) {
      this.$http.delete(`/v1/companies/${this.selectedCompany.value}/users/${id}`)
        .then(() => {
          this.fetchUsers(this.selectedCompany.value, this, users => {
            this.existingUsers = users
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'User removed',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    deleteInvitation(id) {
      this.$http.delete(`/v1/companies/${this.selectedCompany.value}/invitations/${id}`)
        .then(() => {
          this.fetchInvitedUsers(this.selectedCompany.value, this, users => {
            this.invitedUsers = users
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Invitation removed',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = 'app-user'

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const {
      fetchUsers,
      fetchInvitedUsers,
      tableColumns,
      totalUsers,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
    } = useUsersList()

    return {

      fetchUsers,
      fetchInvitedUsers,
      tableColumns,
      totalUsers,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,

      // Filter
      avatarText,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
